import { formatDistanceToNow } from 'date-fns';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Link2,
  Loader2,
  Play,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Switch, SwitchField } from '@/components/ui/catalyst/switch';
import { cn } from '@/lib/utils';
import {
  BackfillProgressSchema,
  RuleBackfillRunSchema,
  RuleBackfillRunStatus,
  useExternalTaskRuleCancelBackfillMutation,
  useExternalTaskRuleGetBackfillProgressQuery,
  useExternalTaskRuleListBackfillsQuery,
  useExternalTaskRuleStartBackfillMutation,
  useGetFirmQuery,
  useUpdateFirmMutation,
} from '@/services/api/firmsService';

interface BackfillPanelProps {
  firmId: string;
  enabledRulesCount: number;
}

export const BackfillPanel: React.FC<BackfillPanelProps> = ({
  firmId,
  enabledRulesCount,
}) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [autoBackfillOnImport, setAutoBackfillOnImport] = useState(false);

  const { data: firm } = useGetFirmQuery(firmId);
  const [updateFirm, { isLoading: isUpdatingFirm }] = useUpdateFirmMutation();

  useEffect(() => {
    if (typeof firm?.auto_backfill_external_task_rules_on_import === 'boolean') {
      setAutoBackfillOnImport(firm.auto_backfill_external_task_rules_on_import);
    }
  }, [firm?.auto_backfill_external_task_rules_on_import]);

  // Fetch backfill history
  const { data: backfillRuns = [], refetch: refetchRuns } =
    useExternalTaskRuleListBackfillsQuery({ firmId });

  // Find any running backfill
  const runningBackfill = backfillRuns.find(
    (run) => run.status === 'RUNNING' || run.status === 'PENDING',
  );

  // Set active run ID when we find a running backfill
  useEffect(() => {
    if (runningBackfill) {
      setActiveRunId(runningBackfill.id);
    }
  }, [runningBackfill]);

  // Poll for progress when there's an active run
  const { data: progress } = useExternalTaskRuleGetBackfillProgressQuery(
    { firmId, runId: activeRunId ?? '' },
    {
      skip: !activeRunId,
      pollingInterval: activeRunId ? 2000 : 0, // Poll every 2 seconds
    },
  );

  // Refresh runs list when progress changes to completed/failed/cancelled
  useEffect(() => {
    if (
      progress &&
      (progress.status === 'COMPLETED' ||
        progress.status === 'FAILED' ||
        progress.status === 'CANCELLED')
    ) {
      setActiveRunId(null);
      refetchRuns();
    }
  }, [progress, refetchRuns]);

  const [startBackfill, { isLoading: isStarting }] =
    useExternalTaskRuleStartBackfillMutation();
  const [cancelBackfill, { isLoading: isCancelling }] =
    useExternalTaskRuleCancelBackfillMutation();

  const handleStartBackfill = async () => {
    try {
      const result = await startBackfill({ firmId }).unwrap();
      setActiveRunId(result.id);
      setShowConfirmDialog(false);
      toast.success('Backfill started successfully');
      refetchRuns();
    } catch {
      toast.error('Failed to start backfill');
    }
  };

  const handleCancelBackfill = async () => {
    if (!activeRunId) return;
    try {
      await cancelBackfill({ firmId, runId: activeRunId }).unwrap();
      toast.success('Backfill cancelled');
      setActiveRunId(null);
      refetchRuns();
    } catch {
      toast.error('Failed to cancel backfill');
    }
  };

  const isRunning = !!runningBackfill || !!activeRunId;

  const handleAutoBackfillToggle = async (checked: boolean) => {
    setAutoBackfillOnImport(checked);
    try {
      await updateFirm({
        id: firmId,
        auto_backfill_external_task_rules_on_import: checked,
      }).unwrap();
      toast.success(
        checked ? 'Auto backfill on import enabled' : 'Auto backfill on import disabled',
      );
    } catch {
      setAutoBackfillOnImport(!checked);
      toast.error('Failed to update auto backfill setting');
    }
  };

  return (
    <>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-5 py-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100">
              <RefreshCw className="h-4 w-4 text-slate-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">
                Bulk Backfill
              </h3>
              <p className="text-xs text-slate-500">
                Apply rules to existing tasks
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4 p-5">
          <p className="text-sm text-slate-600">
            Apply all enabled rules to existing tasks. This will scan all
            matters and create missing links based on your rule configurations.
          </p>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <SwitchField>
              <span data-slot="label" className="text-sm font-medium text-slate-900">
                Auto backfill on import
              </span>
              <span data-slot="description" className="text-xs text-slate-600">
                Automatically start a bulk backfill after CMS task imports finish ingestion.
              </span>
              <Switch
                checked={autoBackfillOnImport}
                onChange={handleAutoBackfillToggle}
                disabled={!firm || isUpdatingFirm}
                color="blue"
              />
            </SwitchField>
          </div>

          {/* Running State */}
          {isRunning && progress && (
            <BackfillProgress
              progress={progress}
              onCancel={handleCancelBackfill}
              isCancelling={isCancelling}
            />
          )}

          {/* Idle State */}
          {!isRunning && (
            <Button
              onClick={() => setShowConfirmDialog(true)}
              disabled={isStarting || enabledRulesCount === 0}
              className="w-full gap-2"
            >
              <Play className="h-4 w-4" />
              Run Backfill
            </Button>
          )}

          {enabledRulesCount === 0 && !isRunning && (
            <p className="text-center text-xs text-amber-600">
              Enable at least one rule before running a backfill.
            </p>
          )}

          {/* Recent Runs */}
          {backfillRuns.length > 0 && (
            <BackfillHistory runs={backfillRuns.slice(0, 3)} />
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Run Bulk Backfill</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                This will apply all {enabledRulesCount} enabled rule
                {enabledRulesCount !== 1 ? 's' : ''} to existing tasks across
                all matters.
              </p>
              <p>
                The process runs in the background and may take several minutes
                depending on the number of matters.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isStarting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleStartBackfill}
              disabled={isStarting}
            >
              {isStarting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Starting...
                </>
              ) : (
                'Start Backfill'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

interface BackfillProgressProps {
  progress: BackfillProgressSchema;
  onCancel: () => void;
  isCancelling: boolean;
}

const BackfillProgress: React.FC<BackfillProgressProps> = ({
  progress,
  onCancel,
  isCancelling,
}) => {
  const percentage =
    progress.total_matters > 0
      ? Math.round((progress.processed_matters / progress.total_matters) * 100)
      : 0;

  return (
    <div className="space-y-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-blue-700">
          <Loader2 className="h-4 w-4 animate-spin" />
          Backfill in Progress
        </div>
        <button
          type="button"
          onClick={onCancel}
          disabled={isCancelling}
          className="text-xs font-medium text-slate-500 transition-colors hover:text-red-600"
        >
          {isCancelling ? 'Cancelling...' : 'Cancel'}
        </button>
      </div>

      {/* Progress Bar */}
      <div className="h-2 overflow-hidden rounded-full bg-blue-100">
        <div
          className="h-full rounded-full bg-blue-500 transition-all duration-300"
          style={{ width: `${percentage}%` }}
        />
      </div>

      <div className="flex items-center justify-between text-xs text-slate-600">
        <span>
          {progress.processed_matters.toLocaleString()} /{' '}
          {progress.total_matters.toLocaleString()} matters
        </span>
        <span className="flex items-center gap-1.5">
          <Link2 className="h-3.5 w-3.5" />
          {progress.total_links_created.toLocaleString()} links created
        </span>
      </div>
    </div>
  );
};

interface BackfillHistoryProps {
  runs: RuleBackfillRunSchema[];
}

const BackfillHistory: React.FC<BackfillHistoryProps> = ({ runs }) => {
  // Filter out currently running ones (they're shown in progress)
  const completedRuns = runs.filter(
    (run) => run.status !== 'RUNNING' && run.status !== 'PENDING',
  );

  if (completedRuns.length === 0) return null;

  return (
    <div className="space-y-2 border-t border-slate-100 pt-4">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
        Recent Runs
      </h4>
      <div className="space-y-2">
        {completedRuns.map((run) => (
          <BackfillRunItem key={run.id} run={run} />
        ))}
      </div>
    </div>
  );
};

interface BackfillRunItemProps {
  run: RuleBackfillRunSchema;
}

const BackfillRunItem: React.FC<BackfillRunItemProps> = ({ run }) => {
  const statusConfig = getStatusConfig(run.status);
  const StatusIcon = statusConfig.icon;

  const getTimeAgo = (): string => {
    if (run.completed_at) {
      return formatDistanceToNow(new Date(run.completed_at), {
        addSuffix: true,
      });
    }
    if (run.created_at) {
      return formatDistanceToNow(new Date(run.created_at), {
        addSuffix: true,
      });
    }
    return '';
  };
  const timeAgo = getTimeAgo();

  return (
    <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2.5">
      <div className="flex items-center gap-2.5">
        <StatusIcon className={cn('h-4 w-4', statusConfig.iconColor)} />
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-[10px] font-semibold',
            statusConfig.badgeClass,
          )}
        >
          {statusConfig.label}
        </span>
      </div>
      <div className="flex items-center gap-4 text-xs text-slate-500">
        <span className="flex items-center gap-1.5">
          <Link2 className="h-3.5 w-3.5" />
          {run.total_links_created.toLocaleString()}
        </span>
        <span className="text-slate-400">{timeAgo}</span>
      </div>
    </div>
  );
};

function getStatusConfig(status: RuleBackfillRunStatus): {
  icon: React.ElementType;
  iconColor: string;
  label: string;
  badgeClass: string;
} {
  switch (status) {
    case 'COMPLETED':
      return {
        icon: CheckCircle2,
        iconColor: 'text-emerald-500',
        label: 'Completed',
        badgeClass: 'bg-emerald-100 text-emerald-700',
      };
    case 'CANCELLED':
      return {
        icon: XCircle,
        iconColor: 'text-slate-400',
        label: 'Cancelled',
        badgeClass: 'bg-slate-100 text-slate-600',
      };
    case 'FAILED':
      return {
        icon: AlertCircle,
        iconColor: 'text-red-500',
        label: 'Failed',
        badgeClass: 'bg-red-100 text-red-700',
      };
    case 'RUNNING':
      return {
        icon: Loader2,
        iconColor: 'text-blue-500',
        label: 'Running',
        badgeClass: 'bg-blue-100 text-blue-700',
      };
    case 'PENDING':
      return {
        icon: Clock,
        iconColor: 'text-amber-500',
        label: 'Pending',
        badgeClass: 'bg-amber-100 text-amber-700',
      };
    default:
      return {
        icon: Clock,
        iconColor: 'text-slate-400',
        label: status,
        badgeClass: 'bg-slate-100 text-slate-600',
      };
  }
}
